import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { splitTitle } from "./Services";

function StockBadge({ stock }) {
  if (stock === null || stock === undefined) return null;
  if (stock <= 0) {
    return <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-400 border border-red-400/40 bg-red-400/10 px-2.5 py-1">Sold Out</span>;
  }
  if (stock <= 2) {
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson border border-crimson/50 bg-crimson/10 px-2.5 py-1 animate-pulse">
        Only {stock} left
      </span>
    );
  }
  return <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-1">In Stock ({stock})</span>;
}

function parsePrice(price) {
  const n = parseFloat((price || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export default function Parts({ onEnquire, content }) {
  const [parts, setParts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loaded, setLoaded] = useState(false);
  const [buyingId, setBuyingId] = useState(null);

  useEffect(() => {
    api.get("/parts")
      .then((res) => setParts(res.data))
      .catch(() => setParts([]))
      .finally(() => setLoaded(true));
  }, []);

  const filters = useMemo(
    () => ["All", ...new Set(parts.map((p) => p.category).filter(Boolean))],
    [parts]
  );

  const visible = useMemo(
    () => (filter === "All" ? parts : parts.filter((p) => p.category === filter)),
    [filter, parts]
  );

  const buy = async (part) => {
    setBuyingId(part.id);
    try {
      const { data } = await api.post("/payments/checkout", { part_id: part.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Checkout failed", { description: formatApiError(err) });
      setBuyingId(null);
    }
  };

  const eyebrow = content?.parts_eyebrow || "01.5 /// THE PARTS SHELF";
  const [titleA, titleB] = splitTitle(content?.parts_title || "Parts & Packages");

  if (loaded && parts.length === 0) return null;

  return (
    <section id="parts" className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32 -mt-8" data-testid="parts-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-wrap items-end justify-between gap-8 border-t border-[#263342] pt-20"
      >
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-crimson font-semibold mb-4">{eyebrow}</p>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-100">
            {titleA} <span className="text-outline">{titleB}</span>
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="parts-filter-chips">
          {filters.map((f) => (
            <button
              key={f}
              data-testid={`parts-filter-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${
                filter === f
                  ? "border-crimson bg-crimson/10 text-crimson"
                  : "border-[#263342] text-slate-400 hover:border-slate-500 hover:text-slate-200"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </motion.div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="parts-grid">
        {visible.map((p, i) => {
          const soldOut = p.stock !== null && p.stock !== undefined && p.stock <= 0;
          const buyable = !soldOut && parsePrice(p.price) > 0;
          return (
            <motion.article
              key={p.id}
              data-testid={`parts-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`group bg-panel border border-[#263342] flex flex-col justify-between min-h-[220px] overflow-hidden hover:border-crimson/50 hover:bg-[#1E2733] transition-colors ${soldOut ? "opacity-60" : ""}`}
            >
              {p.image && (
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161D26] via-transparent to-transparent" />
                </div>
              )}
              <div className="p-7 flex flex-col justify-between flex-1">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 border border-[#263342] px-2.5 py-1">{p.category}</span>
                    <StockBadge stock={p.stock} />
                    <span className="ml-auto font-mono text-xs tracking-[0.15em] text-crimson font-semibold" data-testid={`parts-price-${i}`}>{p.price}</span>
                  </div>
                  <h3 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100 leading-tight">{p.name}</h3>
                  {p.fitment && <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">FITS /// {p.fitment}</p>}
                  {p.desc && <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.desc}</p>}
                </div>
                <div className="mt-6 flex items-center gap-2">
                  {buyable && (
                    <button
                      data-testid={`parts-buy-${i}`}
                      onClick={() => buy(p)}
                      disabled={buyingId === p.id}
                      className="flex items-center gap-2 bg-crimson text-white font-display font-semibold uppercase tracking-widest text-xs px-5 py-2.5 hover:-translate-y-0.5 transition-transform disabled:opacity-60"
                    >
                      <CreditCard size={14} /> {buyingId === p.id ? "Redirecting..." : "Buy Now"}
                    </button>
                  )}
                  <button
                    data-testid={`parts-enquire-${i}`}
                    disabled={soldOut}
                    onClick={() => onEnquire({ service: "Part Enquiry", message: `I'm interested in the ${p.name} (${p.price}). Please contact me with details.` })}
                    className="font-mono text-xs uppercase tracking-[0.2em] border border-[#263342] px-4 py-2.5 text-slate-300 hover:border-crimson hover:text-crimson transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#263342] disabled:hover:text-slate-300"
                  >
                    {soldOut ? "Sold Out" : "Enquire"}
                  </button>
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}
